"use client";

import { useState } from "react";
import { Feld, FeldHuelle, grundGroesse, type FeldEigenschaften, type FeldGroesseGrund } from "./feld";

/**
 * Passwortfeld mit Umschalter „anzeigen/verbergen“ (Preline `toggle-password-basic-usage`). Markup und Symbole
 * wie im Original; der Umschalter läuft über React-Zustand statt über das Preline-Plugin, damit das Feld auch
 * dann funktioniert, wenn es erst nach dem Seitenaufbau eingehängt wird (Dialog, Reiter).
 * Der Umschalter hat genau EINEN Zustandsmechanismus: ein wechselndes `aria-label` („Passwort anzeigen“ ↔ „Passwort
 * verbergen“, benennt die Handlung wie das Symbol), kein `aria-pressed` (sonst läse Hilfstechnik den Zustand doppelt).
 * Das Eingabeelement trägt weiter `autoComplete` (z. B. `current-password`, `new-password`).
 */
type PasswortRest = Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "className" | "style" | "type" | "size" | "children">;

const KLASSEN: Record<FeldGroesseGrund, { normal: string; fehler: string }> = {
  standard: {
    normal:
      "py-2.5 sm:py-3 ps-4 pe-10 block w-full bg-layer border-layer-line rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-2.5 sm:py-3 ps-4 pe-16 block w-full bg-layer border-red-500 rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  kompakt: {
    normal:
      "py-1.5 sm:py-2 ps-3 pe-10 block w-full bg-layer border-layer-line rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-1.5 sm:py-2 ps-3 pe-16 block w-full bg-layer border-red-500 rounded-lg sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
};

const UMSCHALTER =
  "absolute inset-y-0 inset-e-0 flex items-center z-20 px-3 cursor-pointer text-muted-foreground rounded-e-md focus:outline-hidden focus:text-primary-focus";

export function Passwortfeld({
  id,
  label,
  labelVersteckt,
  optional,
  hilfe,
  fehler,
  hinweissatz,
  feldhilfe,
  layout,
  groesse = "standard",
  ...rest
}: FeldEigenschaften & PasswortRest) {
  const [sichtbar, setSichtbar] = useState(false);
  return (
    <Feld id={id} label={label} labelVersteckt={labelVersteckt} optional={optional} hilfe={hilfe} fehler={fehler} hinweissatz={hinweissatz} feldhilfe={feldhilfe} layout={layout} groesse={groesse}>
      {(a) => (
        <FeldHuelle>
          {/* Preline: component/toggle-password/toggle-password-basic-usage */}
          <input
            {...rest}
            id={a.id}
            type={sichtbar ? "text" : "password"}
            className={a.fehlerhaft ? KLASSEN[grundGroesse(groesse)].fehler : KLASSEN[grundGroesse(groesse)].normal}
            aria-describedby={a["aria-describedby"]}
            aria-invalid={a["aria-invalid"]}
          />
          <button
            type="button"
            className={UMSCHALTER}
            aria-label={sichtbar ? "Passwort verbergen" : "Passwort anzeigen"}
            onClick={() => setSichtbar((s) => !s)}
          >
            <svg
              className="shrink-0 size-3.5"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {sichtbar ? (
                <>
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </>
              ) : (
                <>
                  <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                  <line x1="2" x2="22" y1="2" y2="22" />
                </>
              )}
            </svg>
          </button>
        </FeldHuelle>
      )}
    </Feld>
  );
}
